import React, { useState, useRef } from 'react';
import { Button, Modal, Alert, message } from 'antd';
import { QrcodeOutlined, CloseOutlined } from '@ant-design/icons';
import QrScanner from 'react-qr-scanner';

const QRDeviceScanner = ({ 
  devices = [], 
  onDeviceFound, 
  deviceType = 2, // 1 for control panel, 2 for wheel hub sensors
  size = "small",
  style = {}
}) => {
  const [showScanner, setShowScanner] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [hasWebcamSupport, setHasWebcamSupport] = useState(true);
  const [lastScannedCode, setLastScannedCode] = useState(null);
  const [lastScanTimestamp, setLastScanTimestamp] = useState(0);
  const [messageApi, contextHolder] = message.useMessage();
  
  const scanLock = useRef(false);

  const cleanDevEUI = (input) => {
    if (!input) return '';
    const cleanedInput = input.trim().toUpperCase();
    return cleanedInput.replace(/[^A-Z0-9]/g, '');
  };
  
  const isValidDevEUI = (deveui) => {
    if (!deveui || !/^[A-Z0-9]+$/.test(deveui)) return false;
    return deveui.length >= 8 && deveui.length <= 16;
  };

  const startScanner = async () => {
    setCameraError(null);
    
    // Check camera support
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError('Seu navegador não suporta acesso à câmera. Tente em outro navegador ou dispositivo.');
      setHasWebcamSupport(false);
      setShowScanner(true);
      return;
    }
    
    try {
      await navigator.mediaDevices.getUserMedia({ video: true }).then(stream => {
        stream.getTracks().forEach(track => track.stop());
      });
      setShowScanner(true);
      setCameraError(null);
    } catch (err) {
      console.warn('Erro ao verificar câmera:', err);
      let errorMessage = 'Erro ao acessar a câmera. Verifique as permissões e tente novamente.';
      
      if (err?.name === 'NotAllowedError') {
        errorMessage = 'Permissão de câmera negada. Por favor, permita o acesso à câmera.';
      } else if (err?.name === 'NotFoundError') {
        errorMessage = 'Câmera não encontrada. Verifique se há uma câmera conectada.';
      }
      
      setCameraError(errorMessage);
      setShowScanner(true);
    }
  };

  const stopScanner = () => {
    setShowScanner(false);
    setCameraError(null);
    // Clear scan history when closing scanner
    setLastScannedCode(null);
    setLastScanTimestamp(0);
    scanLock.current = false;
  };

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  const handleQRScan = (result) => {
    if (scanLock.current) return;
    
    let scannedData = null;
    if (result && result.text) {
      scannedData = result.text.trim();
    } else if (result && typeof result === 'string') {
      scannedData = result.trim();
    }
    
    if (scannedData) {
      const cleanedData = cleanDevEUI(scannedData);
      
      if (!isValidDevEUI(cleanedData)) {
        console.warn('QR Code não contém um DevEUI válido');
        messageApi.error('Código QR inválido. O DevEUI deve conter entre 8 e 16 caracteres alfanuméricos.');
        return;
      }
      
      scannedData = cleanedData;
      const now = Date.now();
      if (scannedData === lastScannedCode && (now - lastScanTimestamp) < 3000) {
        console.log('Ignorando leitura duplicada do QR Code');
        return;
      }
      
      scanLock.current = true;
      console.log('QR Code detectado (após limpeza):', scannedData);
      
      setLastScannedCode(scannedData);
      setLastScanTimestamp(now);
      
      // Find device by DevEUI
      const foundDevice = devices
        .filter(d => d.cdtype === deviceType)
        .find(d => d.dsdeveui === scannedData);
      
      if (foundDevice) {
        setShowScanner(false);
        setCameraError(null);
        messageApi.success(`Dispositivo ${foundDevice.dsdeveui} encontrado!`);
        onDeviceFound(foundDevice);
        
        // Clear scan data after successful scan
        setTimeout(() => {
          setLastScannedCode(null);
          setLastScanTimestamp(0);
          scanLock.current = false;
        }, 1000);
      } else {
        messageApi.error(`Dispositivo ${scannedData} não encontrado ou não é do tipo correto.`);
        scanLock.current = false;
      }
    }
  };

  const handleQRError = (err) => {
    console.warn('Erro no scanner QR:', err);
    if (err?.name === 'NotAllowedError') {
      setCameraError('Permissão de câmera negada. Por favor, permita o acesso à câmera.');
    } else if (err?.name === 'NotFoundError') {
      setCameraError('Câmera não encontrada. Verifique se há uma câmera conectada.');
    } else if (err?.message?.includes('getUserMedia') || err?.toString().includes('getUserMedia')) {
      setCameraError('Seu navegador não suporta acesso à câmera ou a API está indisponível.');
    } else {
      setCameraError('Erro ao acessar a câmera. Verifique as permissões e tente novamente.');
    }
  };

  return (
    <>
      {contextHolder}
      <Button
        icon={<QrcodeOutlined />}
        onClick={startScanner}
        title="Escanear QR Code"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: 0,
          ...style
        }}
      />

      <Modal
        title="Scanner de QR Code"
        open={showScanner}
        onCancel={stopScanner}
        width={500}
        centered
        destroyOnClose
        maskClosable={false}
        footer={[
          <Button key="toggle" onClick={toggleCamera}>
            Alternar Câmera
          </Button>,
          <Button key="close" onClick={stopScanner} icon={<CloseOutlined />}>
            Fechar
          </Button>
        ]}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          {cameraError ? (
            <Alert
              message="Erro na Câmera"
              description={cameraError}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              action={
                <Button size="small" onClick={startScanner}>
                  Tentar Novamente
                </Button>
              }
            />
          ) : (
            <div style={{ 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px',
              border: '2px dashed #1890ff',
              borderRadius: '8px',
              backgroundColor: '#fafafa',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {hasWebcamSupport ? (
                <QrScanner
                  delay={1000}
                  style={{
                    height: 300,
                    width: '100%'
                  }}
                  onError={handleQRError}
                  onScan={handleQRScan}
                  facingMode={facingMode}
                  legacyMode={false}
                  constraints={{
                    video: {
                      facingMode: facingMode,
                      width: { ideal: 1280 },
                      height: { ideal: 720 }
                    }
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <Alert
                    message="Câmera não disponível"
                    description="Seu navegador não suporta acesso à câmera ou está bloqueado. Tente em outro dispositivo ou navegador."
                    type="error"
                    showIcon
                  />
                </div>
              )}
              
              {/* Overlay para ajudar no posicionamento */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '200px',
                height: '200px',
                border: '3px solid #1890ff',
                borderRadius: '12px',
                pointerEvents: 'none',
                background: 'transparent',
                zIndex: 10
              }}>
                {/* Corner markers */}
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  left: '-10px',
                  width: '30px',
                  height: '30px',
                  border: '4px solid #1890ff',
                  borderRight: 'transparent',
                  borderBottom: 'transparent',
                  borderRadius: '4px 0 0 0'
                }} />
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '-10px',
                  width: '30px',
                  height: '30px',
                  border: '4px solid #1890ff',
                  borderLeft: 'transparent',
                  borderBottom: 'transparent',
                  borderRadius: '0 4px 0 0'
                }} />
                <div style={{
                  position: 'absolute',
                  bottom: '-10px',
                  left: '-10px',
                  width: '30px',
                  height: '30px',
                  border: '4px solid #1890ff',
                  borderRight: 'transparent',
                  borderTop: 'transparent',
                  borderRadius: '0 0 0 4px'
                }} />
                <div style={{
                  position: 'absolute',
                  bottom: '-10px',
                  right: '-10px',
                  width: '30px',
                  height: '30px',
                  border: '4px solid #1890ff',
                  borderLeft: 'transparent',
                  borderTop: 'transparent',
                  borderRadius: '0 0 4px 0'
                }} />
              </div>
            </div>
          )}
          
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <span style={{ fontSize: 14, color: '#666' }}>
              📱 Posicione o QR Code dentro da área destacada
            </span>
            <br />
            <span style={{ fontSize: 12, color: '#999' }}>
              Mantenha o código estável por alguns segundos
            </span>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default QRDeviceScanner;
